import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  signal,
  DestroyRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideX } from '@ng-icons/lucide';
import { HlmButton, HlmInput, HlmLabel, HlmSpinnerImports } from '@pagai-fe/ui';
import { PasswordInputComponent } from '../../../shared/components/password-input/password-input.component';
import { AuthenticateStateEnum } from '../../enums/authenticate.enum';
import { AuthService } from '../../services/auth.service';
import { CognitoResetPasswordResult } from '../../interfaces/auth.interfaces';
import {
  passwordStrengthValidator,
  passwordMatchValidator,
} from '../../validators/password.validators';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    NgIcon,
    HlmInput,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    HlmSpinnerImports,
    PasswordInputComponent,
  ],
  providers: [provideIcons({ lucideCheck, lucideX })],
  host: {
    class: 'block',
  },
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent implements OnDestroy {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  /** True if credentials are stored from login attempt (force change password flow) */
  protected readonly hasStoredCredentials = computed(() => this._authService.hasPendingCredentials);

  @Output()
  protected readonly resetPasswordEvent = new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup(
    {
      email: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      temporaryPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      newPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, passwordStrengthValidator],
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    },
    { validators: passwordMatchValidator },
  );

  /** Password validation state for UI feedback */
  protected get passwordValidation(): {
    minLength: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    specialChar: boolean;
  } {
    const password = this.form.get('newPassword')?.value ?? '';
    return {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }

  protected get passwordsMatch(): boolean {
    const newPassword = this.form.get('newPassword')?.value ?? '';
    const confirmPassword = this.form.get('confirmPassword')?.value ?? '';
    return newPassword.length > 0 && newPassword === confirmPassword;
  }

  ngOnDestroy(): void {
    // Clear stored credentials when leaving the component (e.g., going back to login)
    this._authService.clearPendingCredentials();
  }

  protected resetPassword(): void {
    // If we have stored credentials, only validate newPassword and confirmPassword
    if (this.hasStoredCredentials()) {
      if (
        this.form.get('newPassword')?.invalid ||
        this.form.get('confirmPassword')?.invalid ||
        this.form.hasError('passwordMismatch')
      ) {
        return;
      }
    } else if (!this.form.valid) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(undefined);
    this.form.setErrors(null);

    const { newPassword } = this.form.getRawValue();

    if (this.hasStoredCredentials()) {
      // Use stored credentials from login attempt
      this._authService
        .resetPasswordWithStoredCredentials(newPassword)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: (_error: CognitoResetPasswordResult) => {
            this.isLoading.set(false);
            this.errorMessage.set('Errore durante il reset della password');
            this.form.setErrors({});
          },
          complete: () => {
            this.isLoading.set(false);
            // User is already logged in after successful reset; go to the dynamic
            // landing ("/") which routes to the first available module. Force route
            // re-evaluation (login page is also at "/", so a same-URL nav is a no-op).
            void this._router.navigate(['/'], {
              onSameUrlNavigation: 'reload',
            });
          },
        });
    } else {
      // Manual flow - user entered email and temporary password
      const { email, temporaryPassword } = this.form.getRawValue();
      this._authService
        .resetPassword(email, temporaryPassword, newPassword)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: (_error: CognitoResetPasswordResult) => {
            this.isLoading.set(false);
            this.errorMessage.set('Errore durante il reset della password');
            this.form.setErrors({});
          },
          complete: () => {
            this.resetPasswordEvent.emit(AuthenticateStateEnum.SIGN_IN);
            this.isLoading.set(false);
          },
        });
    }
  }

  protected onSignInClick(): void {
    this._authService.clearPendingCredentials();
    this.resetPasswordEvent.emit(AuthenticateStateEnum.SIGN_IN);
  }

  protected onForgotPasswordClick(): void {
    this._authService.clearPendingCredentials();
    this.resetPasswordEvent.emit(AuthenticateStateEnum.FORGOT_PASSWORD);
  }
}
